import type { CSSProperties } from 'react'
import { ICONS, type IconName } from './registry'

export type { IconName }

interface IconProps {
  name: IconName | string
  size?: number
  className?: string
  style?: CSSProperties
  title?: string
}

/**
 * Drop-in replacement for the prototype's <ix-icon> custom element.
 *
 * Reproduces its rendering exactly: the glyph is scaled by size/16 against its
 * own viewBox and painted with fill="currentColor", wrapped in an inline-flex
 * box of exactly size x size. The wrapper keeps the tag name `ix-icon` as a
 * class so `components.css`'s `.ix-signout ix-icon { transform: rotate(90deg) }`
 * rule still matches (see the companion rule in styles/ix-icon-compat.css).
 */
export function Icon({ name, size = 16, className, style, title }: IconProps) {
  const data = (ICONS as Record<string, { viewBox: string; body: string } | undefined>)[name]
  if (!data) return null

  const vb = data.viewBox.split(/\s+/).map(Number)
  const k = size / 16

  return (
    <span
      className={className ? `ix-icon ${className}` : 'ix-icon'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        ...style,
      }}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
    >
      <svg
        width={vb[2] * k}
        height={vb[3] * k}
        viewBox={data.viewBox}
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
        dangerouslySetInnerHTML={{ __html: data.body }}
      />
    </span>
  )
}

export default Icon
